/**
 * Clamps a CSS color string to the nearest allowed color, based on hue.
 * The allowed colors must be in the form "rgb(r, g, b)".
 *
 * Returns null (= "default / no color") for near-achromatic inputs like
 * blacks and grays.
 *
 * `achromaticThreshold` is the HSV saturation below which a color is treated
 * as achromatic. Highlight colors are pale pastels with low saturation, so
 * they need a lower threshold than the more vivid text colors.
 */
export function clampColor(
  allowedColors: string[],
  color: string,
  achromaticThreshold = 0.15,
): string | null {
  // Fast path: already an allowed value.
  if (allowedColors.includes(color)) return color

  const rgb = parseHtmlColor(color)
  if (!rgb) return null

  // Use HSV saturation to detect achromatic colors (blacks, grays, whites).
  const max = Math.max(...rgb)
  const saturation = max === 0 ? 0 : (max - Math.min(...rgb)) / max
  if (saturation < achromaticThreshold) return null

  // Find the allowed color whose hue is closest on the color wheel.
  const hue = rgbToHue(rgb)
  let nearest: string | null = null
  let minDist = Infinity
  for (const allowedColor of allowedColors) {
    const allowedRgb = parseRgb(allowedColor)
    if (!allowedRgb) continue
    const d = hueDist(hue, rgbToHue(allowedRgb))
    if (d < minDist) {
      minDist = d
      nearest = allowedColor
    }
  }
  return nearest
}

const colorTestingDiv = document.createElement("div")
colorTestingDiv.style.display = "none"
document.body.appendChild(colorTestingDiv)

// Parse any HTML color into a [r, g, b] triple.
function parseHtmlColor(color: string): [number, number, number] | null {
  colorTestingDiv.style.color = color
  const rgbColor = window.getComputedStyle(colorTestingDiv).color
  return parseRgb(rgbColor)
}

// Parse "rgb(r, g, b)" into a [r, g, b] triple.
function parseRgb(color: string): [number, number, number] | null {
  const m = color.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/)
  if (!m) return null
  return [parseInt(m[1]), parseInt(m[2]), parseInt(m[3])]
}

// Convert an RGB triple to a hue in [0, 360).
function rgbToHue([r, g, b]: [r: number, g: number, b: number]): number {
  const rn = r / 255,
    gn = g / 255,
    bn = b / 255
  const max = Math.max(rn, gn, bn),
    min = Math.min(rn, gn, bn)
  const delta = max - min
  if (delta === 0) return 0
  let hue: number
  if (max === rn) hue = ((gn - bn) / delta) % 6
  else if (max === gn) hue = (bn - rn) / delta + 2
  else hue = (rn - gn) / delta + 4
  return (hue * 60 + 360) % 360
}

// Angular distance between two hues on the color wheel.
function hueDist(a: number, b: number): number {
  const d = Math.abs(a - b) % 360
  return d > 180 ? 360 - d : d
}
